import type { LocationCoordinates } from '@/services/location';

export type BaselineLandmarkSeed = {
  name: string;
  address: string;
  coordinates: LocationCoordinates;
};

type CityBaseline = {
  countryCode: string;
  landmarks: BaselineLandmarkSeed[];
};

const BASELINE_CITY_LANDMARKS: Record<string, CityBaseline> = {
  rome: {
    countryCode: 'IT',
    landmarks: [
      { name: 'Colosseum', address: 'Piazza del Colosseo, Rome', coordinates: { latitude: 41.8902, longitude: 12.4922 } },
      { name: 'Trevi Fountain', address: 'Piazza di Trevi, Rome', coordinates: { latitude: 41.9009, longitude: 12.4833 } },
      { name: 'Pantheon', address: 'Piazza della Rotonda, Rome', coordinates: { latitude: 41.8986, longitude: 12.4769 } },
      { name: 'Roman Forum', address: 'Via della Salara Vecchia, Rome', coordinates: { latitude: 41.8925, longitude: 12.4853 } },
      { name: 'Spanish Steps', address: 'Piazza di Spagna, Rome', coordinates: { latitude: 41.9059, longitude: 12.4823 } },
      { name: 'Piazza Navona', address: 'Piazza Navona, Rome', coordinates: { latitude: 41.8992, longitude: 12.4731 } },
      { name: 'St. Peter Basilica', address: 'Piazza San Pietro, Vatican City', coordinates: { latitude: 41.9022, longitude: 12.4539 } },
      { name: 'Castel Sant Angelo', address: 'Lungotevere Castello, Rome', coordinates: { latitude: 41.9031, longitude: 12.4663 } },
    ],
  },
  paris: {
    countryCode: 'FR',
    landmarks: [
      { name: 'Eiffel Tower', address: 'Champ de Mars, Paris', coordinates: { latitude: 48.8584, longitude: 2.2945 } },
      { name: 'Louvre Museum', address: 'Rue de Rivoli, Paris', coordinates: { latitude: 48.8606, longitude: 2.3376 } },
      { name: 'Notre-Dame Cathedral', address: 'Parvis Notre-Dame, Paris', coordinates: { latitude: 48.853, longitude: 2.3499 } },
      { name: 'Arc de Triomphe', address: 'Place Charles de Gaulle, Paris', coordinates: { latitude: 48.8738, longitude: 2.295 } },
      { name: 'Sacre-Coeur', address: 'Rue du Chevalier de la Barre, Paris', coordinates: { latitude: 48.8867, longitude: 2.3431 } },
      { name: 'Musee d Orsay', address: 'Rue de la Legion d Honneur, Paris', coordinates: { latitude: 48.86, longitude: 2.3266 } },
      { name: 'Luxembourg Gardens', address: 'Rue de Medicis, Paris', coordinates: { latitude: 48.8462, longitude: 2.3372 } },
      { name: 'Place de la Concorde', address: 'Place de la Concorde, Paris', coordinates: { latitude: 48.8656, longitude: 2.3212 } },
    ],
  },
  london: {
    countryCode: 'GB',
    landmarks: [
      { name: 'Big Ben', address: 'Westminster, London', coordinates: { latitude: 51.5007, longitude: -0.1246 } },
      { name: 'Tower of London', address: 'Tower Hill, London', coordinates: { latitude: 51.5081, longitude: -0.0759 } },
      { name: 'Buckingham Palace', address: 'Buckingham Palace Rd, London', coordinates: { latitude: 51.5014, longitude: -0.1419 } },
      { name: 'London Eye', address: 'Riverside Building, London', coordinates: { latitude: 51.5033, longitude: -0.1195 } },
      { name: 'St Paul Cathedral', address: 'St. Pauls Churchyard, London', coordinates: { latitude: 51.5138, longitude: -0.0984 } },
      { name: 'Westminster Abbey', address: 'Dean s Yard, London', coordinates: { latitude: 51.4993, longitude: -0.1273 } },
      { name: 'British Museum', address: 'Great Russell Street, London', coordinates: { latitude: 51.5194, longitude: -0.127 } },
      { name: 'Tower Bridge', address: 'Tower Bridge Rd, London', coordinates: { latitude: 51.5055, longitude: -0.0754 } },
    ],
  },
  'new york': {
    countryCode: 'US',
    landmarks: [
      { name: 'Statue of Liberty', address: 'Liberty Island, New York', coordinates: { latitude: 40.6892, longitude: -74.0445 } },
      { name: 'Times Square', address: 'Manhattan, New York', coordinates: { latitude: 40.758, longitude: -73.9855 } },
      { name: 'Central Park', address: 'Manhattan, New York', coordinates: { latitude: 40.7829, longitude: -73.9654 } },
      { name: 'Empire State Building', address: '20 W 34th St, New York', coordinates: { latitude: 40.7484, longitude: -73.9857 } },
      { name: 'Brooklyn Bridge', address: 'Brooklyn Bridge, New York', coordinates: { latitude: 40.7061, longitude: -73.9969 } },
      { name: 'Grand Central Terminal', address: '89 E 42nd St, New York', coordinates: { latitude: 40.7527, longitude: -73.9772 } },
      { name: 'Rockefeller Center', address: '45 Rockefeller Plaza, New York', coordinates: { latitude: 40.7587, longitude: -73.9787 } },
      { name: 'One World Observatory', address: '117 West St, New York', coordinates: { latitude: 40.7127, longitude: -74.0134 } },
    ],
  },
  chicago: {
    countryCode: 'US',
    landmarks: [
      { name: 'Millennium Park', address: '201 E Randolph St, Chicago, IL', coordinates: { latitude: 41.8826, longitude: -87.6226 } },
      { name: 'Willis Tower Skydeck', address: '233 S Wacker Dr, Chicago, IL', coordinates: { latitude: 41.8789, longitude: -87.6359 } },
      { name: 'Navy Pier', address: '600 E Grand Ave, Chicago, IL', coordinates: { latitude: 41.8917, longitude: -87.6083 } },
      { name: 'Art Institute of Chicago', address: '111 S Michigan Ave, Chicago, IL', coordinates: { latitude: 41.8796, longitude: -87.6237 } },
      { name: 'Field Museum', address: '1400 S Lake Shore Dr, Chicago, IL', coordinates: { latitude: 41.8663, longitude: -87.6169 } },
      { name: '360 Chicago', address: '875 N Michigan Ave, Chicago, IL', coordinates: { latitude: 41.8988, longitude: -87.6229 } },
      { name: 'Buckingham Fountain', address: '301 S Columbus Dr, Chicago, IL', coordinates: { latitude: 41.8758, longitude: -87.6189 } },
      { name: 'Chicago Riverwalk', address: 'Chicago Riverwalk, Chicago, IL', coordinates: { latitude: 41.8874, longitude: -87.6229 } },
    ],
  },
  tokyo: {
    countryCode: 'JP',
    landmarks: [
      { name: 'Tokyo Skytree', address: '1 Chome-1-2 Oshiage, Tokyo', coordinates: { latitude: 35.7101, longitude: 139.8107 } },
      { name: 'Senso-ji Temple', address: '2 Chome-3-1 Asakusa, Tokyo', coordinates: { latitude: 35.7148, longitude: 139.7967 } },
      { name: 'Shibuya Crossing', address: 'Shibuya, Tokyo', coordinates: { latitude: 35.6595, longitude: 139.7004 } },
      { name: 'Tokyo Tower', address: '4 Chome-2-8 Shibakoen, Tokyo', coordinates: { latitude: 35.6586, longitude: 139.7454 } },
      { name: 'Meiji Shrine', address: '1-1 Yoyogikamizonocho, Tokyo', coordinates: { latitude: 35.6764, longitude: 139.6993 } },
      { name: 'Tokyo Station', address: '1 Chome Marunouchi, Tokyo', coordinates: { latitude: 35.6812, longitude: 139.7671 } },
      { name: 'Ueno Park', address: 'Uenokoen, Taito City, Tokyo', coordinates: { latitude: 35.7156, longitude: 139.773 } },
      { name: 'Imperial Palace East Gardens', address: '1-1 Chiyoda, Tokyo', coordinates: { latitude: 35.6852, longitude: 139.7528 } },
    ],
  },
  barcelona: {
    countryCode: 'ES',
    landmarks: [
      { name: 'Sagrada Familia', address: 'Carrer de Mallorca, Barcelona', coordinates: { latitude: 41.4036, longitude: 2.1744 } },
      { name: 'Park Guell', address: 'Carrer d Olot, Barcelona', coordinates: { latitude: 41.4145, longitude: 2.1527 } },
      { name: 'La Rambla', address: 'La Rambla, Barcelona', coordinates: { latitude: 41.3809, longitude: 2.1732 } },
      { name: 'Casa Batllo', address: 'Passeig de Gracia, Barcelona', coordinates: { latitude: 41.3917, longitude: 2.1649 } },
      { name: 'Barcelona Cathedral', address: 'Pla de la Seu, Barcelona', coordinates: { latitude: 41.3839, longitude: 2.1763 } },
      { name: 'Magic Fountain of Montjuic', address: 'Pla Carles Buigas, Barcelona', coordinates: { latitude: 41.3712, longitude: 2.1519 } },
      { name: 'Arc de Triomf', address: 'Passeig de Lluis Companys, Barcelona', coordinates: { latitude: 41.391, longitude: 2.1802 } },
      { name: 'Barceloneta Beach', address: 'Passeig Maritim de la Barceloneta, Barcelona', coordinates: { latitude: 41.378, longitude: 2.1925 } },
    ],
  },
  amsterdam: {
    countryCode: 'NL',
    landmarks: [
      { name: 'Rijksmuseum', address: 'Museumstraat 1, Amsterdam', coordinates: { latitude: 52.36, longitude: 4.8852 } },
      { name: 'Van Gogh Museum', address: 'Museumplein 6, Amsterdam', coordinates: { latitude: 52.3584, longitude: 4.8811 } },
      { name: 'Anne Frank House', address: 'Westermarkt 20, Amsterdam', coordinates: { latitude: 52.3752, longitude: 4.8839 } },
      { name: 'Dam Square', address: 'Dam, Amsterdam', coordinates: { latitude: 52.3731, longitude: 4.8922 } },
      { name: 'Vondelpark', address: 'Vondelpark, Amsterdam', coordinates: { latitude: 52.3583, longitude: 4.8687 } },
      { name: 'Heineken Experience', address: 'Stadhouderskade 78, Amsterdam', coordinates: { latitude: 52.3579, longitude: 4.891 } },
      { name: 'NEMO Science Museum', address: 'Oosterdok 2, Amsterdam', coordinates: { latitude: 52.374, longitude: 4.9124 } },
      { name: 'Jordaan Canals', address: 'Jordaan, Amsterdam', coordinates: { latitude: 52.3742, longitude: 4.8799 } },
    ],
  },
  dubai: {
    countryCode: 'AE',
    landmarks: [
      { name: 'Burj Khalifa', address: '1 Sheikh Mohammed bin Rashid Blvd, Dubai', coordinates: { latitude: 25.1972, longitude: 55.2744 } },
      { name: 'Dubai Mall', address: 'Downtown Dubai', coordinates: { latitude: 25.1985, longitude: 55.2796 } },
      { name: 'Dubai Fountain', address: 'Downtown Dubai', coordinates: { latitude: 25.1945, longitude: 55.2797 } },
      { name: 'Burj Al Arab Viewpoint', address: 'Jumeirah Beach Rd, Dubai', coordinates: { latitude: 25.1412, longitude: 55.1853 } },
      { name: 'Dubai Marina Walk', address: 'Dubai Marina, Dubai', coordinates: { latitude: 25.0776, longitude: 55.1338 } },
      { name: 'Palm Jumeirah Boardwalk', address: 'Palm Jumeirah, Dubai', coordinates: { latitude: 25.1124, longitude: 55.1386 } },
      { name: 'Al Fahidi Historical District', address: 'Al Fahidi, Dubai', coordinates: { latitude: 25.2636, longitude: 55.2993 } },
      { name: 'Museum of the Future', address: 'Sheikh Zayed Rd, Dubai', coordinates: { latitude: 25.2203, longitude: 55.2839 } },
    ],
  },
  singapore: {
    countryCode: 'SG',
    landmarks: [
      { name: 'Marina Bay Sands', address: 'Bayfront Ave, Singapore', coordinates: { latitude: 1.2834, longitude: 103.8607 } },
      { name: 'Gardens by the Bay', address: '18 Marina Gardens Dr, Singapore', coordinates: { latitude: 1.2816, longitude: 103.8636 } },
      { name: 'Merlion Park', address: '1 Fullerton Rd, Singapore', coordinates: { latitude: 1.2868, longitude: 103.8545 } },
      { name: 'Singapore Flyer', address: '30 Raffles Ave, Singapore', coordinates: { latitude: 1.2893, longitude: 103.8631 } },
      { name: 'Chinatown Heritage Area', address: 'Pagoda St, Singapore', coordinates: { latitude: 1.2839, longitude: 103.8442 } },
      { name: 'Clarke Quay', address: '3 River Valley Rd, Singapore', coordinates: { latitude: 1.2906, longitude: 103.8463 } },
      { name: 'Sentosa Merlion Area', address: 'Sentosa Gateway, Singapore', coordinates: { latitude: 1.254, longitude: 103.8218 } },
      { name: 'Botanic Gardens', address: '1 Cluny Rd, Singapore', coordinates: { latitude: 1.3138, longitude: 103.8159 } },
    ],
  },
  istanbul: {
    countryCode: 'TR',
    landmarks: [
      { name: 'Hagia Sophia', address: 'Sultan Ahmet, Istanbul', coordinates: { latitude: 41.0086, longitude: 28.9802 } },
      { name: 'Blue Mosque', address: 'Atmeydani Cd, Istanbul', coordinates: { latitude: 41.0054, longitude: 28.9768 } },
      { name: 'Topkapi Palace', address: 'Cankurtaran, Istanbul', coordinates: { latitude: 41.0115, longitude: 28.9834 } },
      { name: 'Grand Bazaar', address: 'Beyazit, Istanbul', coordinates: { latitude: 41.0107, longitude: 28.9681 } },
      { name: 'Galata Tower', address: 'Bereketzade, Istanbul', coordinates: { latitude: 41.0256, longitude: 28.9742 } },
      { name: 'Basilica Cistern', address: 'Yerebatan Cd, Istanbul', coordinates: { latitude: 41.0084, longitude: 28.9779 } },
      { name: 'Spice Bazaar', address: 'Ruestempasa, Istanbul', coordinates: { latitude: 41.0165, longitude: 28.9707 } },
      { name: 'Dolmabahce Palace', address: 'Vişnezade, Istanbul', coordinates: { latitude: 41.0392, longitude: 29.0007 } },
    ],
  },
  lisbon: {
    countryCode: 'PT',
    landmarks: [
      { name: 'Belem Tower', address: 'Av. Brasilia, Lisbon', coordinates: { latitude: 38.6916, longitude: -9.2159 } },
      { name: 'Jeronimos Monastery', address: 'Praca do Imperio, Lisbon', coordinates: { latitude: 38.6979, longitude: -9.2065 } },
      { name: 'Sao Jorge Castle', address: 'R. de Santa Cruz do Castelo, Lisbon', coordinates: { latitude: 38.7139, longitude: -9.1335 } },
      { name: 'Commerce Square', address: 'Praca do Comercio, Lisbon', coordinates: { latitude: 38.7078, longitude: -9.1366 } },
      { name: 'Alfama District', address: 'Alfama, Lisbon', coordinates: { latitude: 38.711, longitude: -9.1296 } },
      { name: 'Santa Justa Lift', address: 'R. do Ouro, Lisbon', coordinates: { latitude: 38.712, longitude: -9.1393 } },
      { name: 'Miradouro da Senhora do Monte', address: 'Largo Monte, Lisbon', coordinates: { latitude: 38.7193, longitude: -9.1328 } },
      { name: 'Time Out Market Lisboa', address: 'Av. 24 de Julho, Lisbon', coordinates: { latitude: 38.7076, longitude: -9.1467 } },
    ],
  },
  madrid: {
    countryCode: 'ES',
    landmarks: [
      { name: 'Plaza Mayor', address: 'Plaza Mayor, Madrid', coordinates: { latitude: 40.4154, longitude: -3.7074 } },
      { name: 'Royal Palace of Madrid', address: 'Calle de Bailen, Madrid', coordinates: { latitude: 40.4179, longitude: -3.7143 } },
      { name: 'Puerta del Sol', address: 'Puerta del Sol, Madrid', coordinates: { latitude: 40.4169, longitude: -3.7035 } },
      { name: 'Prado Museum', address: 'C. de Ruiz de Alarcon, Madrid', coordinates: { latitude: 40.4138, longitude: -3.6921 } },
      { name: 'Retiro Park', address: 'Plaza de la Independencia, Madrid', coordinates: { latitude: 40.4153, longitude: -3.6844 } },
      { name: 'Gran Via', address: 'Gran Via, Madrid', coordinates: { latitude: 40.4203, longitude: -3.7058 } },
      { name: 'Temple of Debod', address: 'C. de Ferraz, Madrid', coordinates: { latitude: 40.424, longitude: -3.7174 } },
      { name: 'Santiago Bernabeu Stadium', address: 'Av. de Concha Espina, Madrid', coordinates: { latitude: 40.453, longitude: -3.6883 } },
    ],
  },
  prague: {
    countryCode: 'CZ',
    landmarks: [
      { name: 'Charles Bridge', address: 'Karluv most, Prague', coordinates: { latitude: 50.0865, longitude: 14.4114 } },
      { name: 'Prague Castle', address: 'Hradcany, Prague', coordinates: { latitude: 50.0909, longitude: 14.4005 } },
      { name: 'Old Town Square', address: 'Staromestske namesti, Prague', coordinates: { latitude: 50.087, longitude: 14.406 } },
      { name: 'Astronomical Clock', address: 'Staromestske nam., Prague', coordinates: { latitude: 50.087, longitude: 14.4063 } },
      { name: 'Wenceslas Square', address: 'Vaclavske namesti, Prague', coordinates: { latitude: 50.0811, longitude: 14.4276 } },
      { name: 'St. Vitus Cathedral', address: 'III. nadvori, Prague Castle', coordinates: { latitude: 50.0909, longitude: 14.4005 } },
      { name: 'Petrin Tower', address: 'Petrinske sady, Prague', coordinates: { latitude: 50.0835, longitude: 14.3957 } },
      { name: 'Dancing House', address: 'Jiraskovo namesti, Prague', coordinates: { latitude: 50.0755, longitude: 14.414 } },
    ],
  },
  vienna: {
    countryCode: 'AT',
    landmarks: [
      { name: 'Schonbrunn Palace', address: 'Schonbrunner Schlossstrasse, Vienna', coordinates: { latitude: 48.1845, longitude: 16.3122 } },
      { name: 'St. Stephens Cathedral', address: 'Stephansplatz, Vienna', coordinates: { latitude: 48.2082, longitude: 16.3738 } },
      { name: 'Belvedere Palace', address: 'Prinz Eugen-Strasse, Vienna', coordinates: { latitude: 48.1912, longitude: 16.3809 } },
      { name: 'Vienna State Opera', address: 'Opernring 2, Vienna', coordinates: { latitude: 48.2029, longitude: 16.3699 } },
      { name: 'Hofburg Palace', address: 'Michaelerkuppel, Vienna', coordinates: { latitude: 48.2076, longitude: 16.3656 } },
      { name: 'Prater and Giant Ferris Wheel', address: 'Riesenradplatz, Vienna', coordinates: { latitude: 48.2167, longitude: 16.3959 } },
      { name: 'MuseumsQuartier', address: 'Museumsplatz 1, Vienna', coordinates: { latitude: 48.203, longitude: 16.3594 } },
      { name: 'Naschmarkt', address: 'Naschmarkt, Vienna', coordinates: { latitude: 48.1985, longitude: 16.3626 } },
    ],
  },
  budapest: {
    countryCode: 'HU',
    landmarks: [
      { name: 'Parliament Building', address: 'Kossuth Lajos ter 1-3, Budapest', coordinates: { latitude: 47.5079, longitude: 19.0456 } },
      { name: 'Buda Castle', address: 'Szent Gyorgy ter 2, Budapest', coordinates: { latitude: 47.4962, longitude: 19.0399 } },
      { name: 'Fishermans Bastion', address: 'Szentharomsag ter, Budapest', coordinates: { latitude: 47.5022, longitude: 19.0344 } },
      { name: 'Chain Bridge', address: 'Szechenyi Lanchid, Budapest', coordinates: { latitude: 47.498, longitude: 19.0436 } },
      { name: 'St. Stephens Basilica', address: 'Szent Istvan ter 1, Budapest', coordinates: { latitude: 47.5009, longitude: 19.0537 } },
      { name: 'Heroes Square', address: 'Hosok tere, Budapest', coordinates: { latitude: 47.5149, longitude: 19.077 } },
      { name: 'Szechenyi Thermal Bath', address: 'Allatkerti krt. 9-11, Budapest', coordinates: { latitude: 47.5186, longitude: 19.0817 } },
      { name: 'Gellert Hill Citadel', address: 'Citadella setany, Budapest', coordinates: { latitude: 47.4869, longitude: 19.0477 } },
    ],
  },
  athens: {
    countryCode: 'GR',
    landmarks: [
      { name: 'Acropolis of Athens', address: 'Acropolis, Athens', coordinates: { latitude: 37.9715, longitude: 23.7257 } },
      { name: 'Parthenon', address: 'Acropolis, Athens', coordinates: { latitude: 37.9715, longitude: 23.7267 } },
      { name: 'Acropolis Museum', address: 'Dionysiou Areopagitou 15, Athens', coordinates: { latitude: 37.9681, longitude: 23.7286 } },
      { name: 'Temple of Olympian Zeus', address: 'Vasilissis Olgas Ave, Athens', coordinates: { latitude: 37.9694, longitude: 23.7325 } },
      { name: 'Syntagma Square', address: 'Syntagma Square, Athens', coordinates: { latitude: 37.9755, longitude: 23.7348 } },
      { name: 'Plaka District', address: 'Plaka, Athens', coordinates: { latitude: 37.9723, longitude: 23.7298 } },
      { name: 'Lycabettus Hill', address: 'Lycabettus, Athens', coordinates: { latitude: 37.9839, longitude: 23.7432 } },
      { name: 'Ancient Agora of Athens', address: 'Adrianou 24, Athens', coordinates: { latitude: 37.9755, longitude: 23.7225 } },
    ],
  },
  seoul: {
    countryCode: 'KR',
    landmarks: [
      { name: 'Gyeongbokgung Palace', address: '161 Sajik-ro, Seoul', coordinates: { latitude: 37.5796, longitude: 126.977 } },
      { name: 'Bukchon Hanok Village', address: 'Gyedong-gil, Seoul', coordinates: { latitude: 37.5826, longitude: 126.9831 } },
      { name: 'N Seoul Tower', address: '105 Namsangongwon-gil, Seoul', coordinates: { latitude: 37.5512, longitude: 126.9882 } },
      { name: 'Myeongdong Shopping Street', address: 'Myeongdong, Seoul', coordinates: { latitude: 37.5636, longitude: 126.9834 } },
      { name: 'Changdeokgung Palace', address: '99 Yulgok-ro, Seoul', coordinates: { latitude: 37.5794, longitude: 126.991 } },
      { name: 'Insadong Culture Street', address: 'Insadong-gil, Seoul', coordinates: { latitude: 37.574, longitude: 126.9857 } },
      { name: 'Dongdaemun Design Plaza', address: '281 Eulji-ro, Seoul', coordinates: { latitude: 37.5665, longitude: 127.0092 } },
      { name: 'Lotte World Tower Seoul Sky', address: '300 Olympic-ro, Seoul', coordinates: { latitude: 37.5125, longitude: 127.1025 } },
    ],
  },
  bangkok: {
    countryCode: 'TH',
    landmarks: [
      { name: 'Grand Palace', address: 'Na Phra Lan Rd, Bangkok', coordinates: { latitude: 13.7501, longitude: 100.4913 } },
      { name: 'Wat Arun', address: '158 Wang Doem Rd, Bangkok', coordinates: { latitude: 13.7437, longitude: 100.4889 } },
      { name: 'Wat Pho', address: '2 Sanamchai Rd, Bangkok', coordinates: { latitude: 13.7465, longitude: 100.493 } },
      { name: 'Chatuchak Market', address: 'Kamphaeng Phet 2 Rd, Bangkok', coordinates: { latitude: 13.7998, longitude: 100.5501 } },
      { name: 'Jim Thompson House', address: '6 Kasem San 2 Alley, Bangkok', coordinates: { latitude: 13.7493, longitude: 100.5286 } },
      { name: 'ICONSIAM', address: '299 Charoen Nakhon Rd, Bangkok', coordinates: { latitude: 13.7262, longitude: 100.5095 } },
      { name: 'Lumphini Park', address: 'Rama IV Rd, Bangkok', coordinates: { latitude: 13.7306, longitude: 100.5418 } },
      { name: 'Asiatique The Riverfront', address: '2194 Charoen Krung Rd, Bangkok', coordinates: { latitude: 13.7041, longitude: 100.5049 } },
    ],
  },
  'hong kong': {
    countryCode: 'HK',
    landmarks: [
      { name: 'Victoria Peak', address: 'The Peak, Hong Kong', coordinates: { latitude: 22.2759, longitude: 114.1455 } },
      { name: 'Tsim Sha Tsui Promenade', address: 'Tsim Sha Tsui, Hong Kong', coordinates: { latitude: 22.2948, longitude: 114.1719 } },
      { name: 'Star Ferry Pier', address: 'Central, Hong Kong', coordinates: { latitude: 22.2867, longitude: 114.1587 } },
      { name: 'Big Buddha', address: 'Ngong Ping, Lantau Island', coordinates: { latitude: 22.2539, longitude: 113.9049 } },
      { name: 'Temple Street Night Market', address: 'Temple St, Hong Kong', coordinates: { latitude: 22.3076, longitude: 114.1707 } },
      { name: 'Avenue of Stars', address: 'Tsim Sha Tsui, Hong Kong', coordinates: { latitude: 22.2933, longitude: 114.1747 } },
      { name: 'Man Mo Temple', address: '124-126 Hollywood Rd, Hong Kong', coordinates: { latitude: 22.284, longitude: 114.1501 } },
      { name: 'Nan Lian Garden', address: '60 Fung Tak Rd, Hong Kong', coordinates: { latitude: 22.3398, longitude: 114.205 } },
    ],
  },
  sydney: {
    countryCode: 'AU',
    landmarks: [
      { name: 'Sydney Opera House', address: 'Bennelong Point, Sydney', coordinates: { latitude: -33.8568, longitude: 151.2153 } },
      { name: 'Sydney Harbour Bridge', address: 'Sydney Harbour Bridge, Sydney', coordinates: { latitude: -33.8523, longitude: 151.2108 } },
      { name: 'Bondi Beach', address: 'Bondi Beach, Sydney', coordinates: { latitude: -33.8908, longitude: 151.2743 } },
      { name: 'The Rocks', address: 'The Rocks, Sydney', coordinates: { latitude: -33.8599, longitude: 151.209 } },
      { name: 'Darling Harbour', address: 'Darling Harbour, Sydney', coordinates: { latitude: -33.8748, longitude: 151.1989 } },
      { name: 'Royal Botanic Garden Sydney', address: 'Mrs Macquaries Rd, Sydney', coordinates: { latitude: -33.8642, longitude: 151.2166 } },
      { name: 'Taronga Zoo Sydney', address: 'Bradleys Head Rd, Mosman', coordinates: { latitude: -33.843, longitude: 151.241 } },
      { name: 'Queen Victoria Building', address: '455 George St, Sydney', coordinates: { latitude: -33.8716, longitude: 151.2069 } },
    ],
  },
  'los angeles': {
    countryCode: 'US',
    landmarks: [
      { name: 'Hollywood Sign Viewpoint', address: 'Mt Lee Dr, Los Angeles', coordinates: { latitude: 34.1341, longitude: -118.3215 } },
      { name: 'Griffith Observatory', address: '2800 E Observatory Rd, Los Angeles', coordinates: { latitude: 34.1184, longitude: -118.3004 } },
      { name: 'Santa Monica Pier', address: '200 Santa Monica Pier, Santa Monica', coordinates: { latitude: 34.0094, longitude: -118.4973 } },
      { name: 'Venice Beach Boardwalk', address: 'Venice Beach, Los Angeles', coordinates: { latitude: 33.985, longitude: -118.4695 } },
      { name: 'The Getty Center', address: '1200 Getty Center Dr, Los Angeles', coordinates: { latitude: 34.078, longitude: -118.4741 } },
      { name: 'Universal Studios Hollywood', address: '100 Universal City Plaza, Universal City', coordinates: { latitude: 34.1381, longitude: -118.3534 } },
      { name: 'Runyon Canyon Park', address: '2000 N Fuller Ave, Los Angeles', coordinates: { latitude: 34.1177, longitude: -118.3506 } },
      { name: 'LACMA Urban Light', address: '5905 Wilshire Blvd, Los Angeles', coordinates: { latitude: 34.0638, longitude: -118.3592 } },
    ],
  },
  'san francisco': {
    countryCode: 'US',
    landmarks: [
      { name: 'Golden Gate Bridge', address: 'Golden Gate Bridge, San Francisco', coordinates: { latitude: 37.8199, longitude: -122.4783 } },
      { name: 'Alcatraz Landing Pier 33', address: 'Pier 33, San Francisco', coordinates: { latitude: 37.8078, longitude: -122.403 } },
      { name: 'Fishermans Wharf', address: 'Fishermans Wharf, San Francisco', coordinates: { latitude: 37.808, longitude: -122.4177 } },
      { name: 'Lombard Street', address: 'Lombard St, San Francisco', coordinates: { latitude: 37.8021, longitude: -122.4187 } },
      { name: 'Palace of Fine Arts', address: '3601 Lyon St, San Francisco', coordinates: { latitude: 37.8021, longitude: -122.4488 } },
      { name: 'Golden Gate Park', address: 'Golden Gate Park, San Francisco', coordinates: { latitude: 37.7694, longitude: -122.4862 } },
      { name: 'Twin Peaks Viewpoint', address: 'Twin Peaks Blvd, San Francisco', coordinates: { latitude: 37.7544, longitude: -122.4477 } },
      { name: 'Ferry Building Marketplace', address: '1 Ferry Building, San Francisco', coordinates: { latitude: 37.7955, longitude: -122.3937 } },
    ],
  },
  cairo: {
    countryCode: 'EG',
    landmarks: [
      { name: 'Pyramids of Giza', address: 'Al Haram, Giza', coordinates: { latitude: 29.9792, longitude: 31.1342 } },
      { name: 'Great Sphinx of Giza', address: 'Al Haram, Giza', coordinates: { latitude: 29.9753, longitude: 31.1376 } },
      { name: 'Egyptian Museum', address: 'Tahrir Square, Cairo', coordinates: { latitude: 30.0478, longitude: 31.2336 } },
      { name: 'Khan el-Khalili', address: 'El-Gamaleya, Cairo', coordinates: { latitude: 30.0478, longitude: 31.2625 } },
      { name: 'Cairo Citadel', address: 'Salah Salem St, Cairo', coordinates: { latitude: 30.0299, longitude: 31.2615 } },
      { name: 'Mosque of Muhammad Ali', address: 'Citadel Square, Cairo', coordinates: { latitude: 30.0287, longitude: 31.2599 } },
      { name: 'Coptic Cairo', address: 'Mar Girgis, Cairo', coordinates: { latitude: 30.0069, longitude: 31.23 } },
      { name: 'Nile Corniche Walk', address: 'Corniche El Nil, Cairo', coordinates: { latitude: 30.0444, longitude: 31.2357 } },
    ],
  },
  'cape town': {
    countryCode: 'ZA',
    landmarks: [
      { name: 'Table Mountain Cableway', address: 'Tafelberg Rd, Cape Town', coordinates: { latitude: -33.9628, longitude: 18.4098 } },
      { name: 'V and A Waterfront', address: '19 Dock Rd, Cape Town', coordinates: { latitude: -33.9036, longitude: 18.4218 } },
      { name: 'Bo-Kaap', address: 'Bo-Kaap, Cape Town', coordinates: { latitude: -33.9188, longitude: 18.4142 } },
      { name: 'Camps Bay Beach', address: 'Camps Bay, Cape Town', coordinates: { latitude: -33.9501, longitude: 18.3774 } },
      { name: 'Kirstenbosch Gardens', address: 'Rhodes Dr, Newlands', coordinates: { latitude: -33.9888, longitude: 18.4323 } },
      { name: 'Cape Point', address: 'Cape Point Nature Reserve', coordinates: { latitude: -34.3568, longitude: 18.4967 } },
      { name: 'Boulders Beach', address: 'Kleintuin Rd, Simon s Town', coordinates: { latitude: -34.1976, longitude: 18.4516 } },
      { name: 'Signal Hill Viewpoint', address: 'Signal Hill Rd, Cape Town', coordinates: { latitude: -33.9186, longitude: 18.3958 } },
    ],
  },
  osaka: {
    countryCode: 'JP',
    landmarks: [
      { name: 'Osaka Castle', address: '1-1 Osakajo, Chuo Ward, Osaka', coordinates: { latitude: 34.6873, longitude: 135.5262 } },
      { name: 'Dotonbori', address: 'Dotonbori, Chuo Ward, Osaka', coordinates: { latitude: 34.6687, longitude: 135.5022 } },
      { name: 'Umeda Sky Building', address: '1-1-88 Oyodonaka, Kita Ward, Osaka', coordinates: { latitude: 34.7055, longitude: 135.4909 } },
      { name: 'Shitenno-ji Temple', address: '1-11-18 Shitennoji, Tennoji Ward, Osaka', coordinates: { latitude: 34.6545, longitude: 135.5161 } },
      { name: 'Kuromon Market', address: '2 Chome Nipponbashi, Chuo Ward, Osaka', coordinates: { latitude: 34.6654, longitude: 135.5063 } },
      { name: 'Abeno Harukas', address: '1-1-43 Abenosuji, Abeno Ward, Osaka', coordinates: { latitude: 34.6469, longitude: 135.5137 } },
      { name: 'Sumiyoshi Taisha', address: '2-9-89 Sumiyoshi, Sumiyoshi Ward, Osaka', coordinates: { latitude: 34.6132, longitude: 135.4937 } },
      { name: 'Shinsaibashi Shopping Street', address: 'Shinsaibashi-suji, Chuo Ward, Osaka', coordinates: { latitude: 34.672, longitude: 135.5016 } },
    ],
  },
  kyoto: {
    countryCode: 'JP',
    landmarks: [
      { name: 'Fushimi Inari Taisha', address: '68 Fukakusa Yabunouchicho, Kyoto', coordinates: { latitude: 34.9671, longitude: 135.7727 } },
      { name: 'Kinkaku-ji', address: '1 Kinkakujicho, Kita Ward, Kyoto', coordinates: { latitude: 35.0394, longitude: 135.7292 } },
      { name: 'Kiyomizu-dera', address: '294 Kiyomizu, Higashiyama Ward, Kyoto', coordinates: { latitude: 34.9949, longitude: 135.785 } },
      { name: 'Arashiyama Bamboo Grove', address: 'Sagaogurayama Tabuchiyamacho, Kyoto', coordinates: { latitude: 35.017, longitude: 135.6717 } },
      { name: 'Nijo Castle', address: '541 Nijojocho, Nakagyo Ward, Kyoto', coordinates: { latitude: 35.0142, longitude: 135.7482 } },
      { name: 'Gion District', address: 'Gionmachi, Higashiyama Ward, Kyoto', coordinates: { latitude: 35.0037, longitude: 135.7788 } },
      { name: 'Philosophers Path', address: 'Sakyo Ward, Kyoto', coordinates: { latitude: 35.0266, longitude: 135.7988 } },
      { name: 'Nishiki Market', address: 'Nishikikoji-dori, Nakagyo Ward, Kyoto', coordinates: { latitude: 35.005, longitude: 135.7644 } },
    ],
  },
  berlin: {
    countryCode: 'DE',
    landmarks: [
      { name: 'Brandenburg Gate', address: 'Pariser Platz, Berlin', coordinates: { latitude: 52.5163, longitude: 13.3777 } },
      { name: 'Reichstag Building', address: 'Platz der Republik 1, Berlin', coordinates: { latitude: 52.5186, longitude: 13.3762 } },
      { name: 'Berlin Cathedral', address: 'Am Lustgarten, Berlin', coordinates: { latitude: 52.5192, longitude: 13.401 } },
      { name: 'Museum Island', address: 'Museumsinsel, Berlin', coordinates: { latitude: 52.5169, longitude: 13.401 } },
      { name: 'Checkpoint Charlie', address: 'Friedrichstrasse 43-45, Berlin', coordinates: { latitude: 52.5075, longitude: 13.3904 } },
      { name: 'East Side Gallery', address: 'Muhlenstrasse, Berlin', coordinates: { latitude: 52.505, longitude: 13.4399 } },
      { name: 'Charlottenburg Palace', address: 'Spandauer Damm 20-24, Berlin', coordinates: { latitude: 52.5208, longitude: 13.2956 } },
      { name: 'Tiergarten Park', address: 'Tiergarten, Berlin', coordinates: { latitude: 52.5146, longitude: 13.3501 } },
    ],
  },
  dublin: {
    countryCode: 'IE',
    landmarks: [
      { name: 'Trinity College Dublin', address: 'College Green, Dublin', coordinates: { latitude: 53.3438, longitude: -6.2546 } },
      { name: 'Guinness Storehouse', address: "St James's Gate, Dublin", coordinates: { latitude: 53.3419, longitude: -6.2869 } },
      { name: "St Patrick's Cathedral", address: 'St Patrick s Close, Dublin', coordinates: { latitude: 53.3395, longitude: -6.2715 } },
      { name: 'Dublin Castle', address: 'Dame St, Dublin', coordinates: { latitude: 53.343, longitude: -6.2672 } },
      { name: 'Temple Bar', address: 'Temple Bar, Dublin', coordinates: { latitude: 53.3455, longitude: -6.2646 } },
      { name: 'Kilmainham Gaol', address: 'Inchicore Rd, Dublin', coordinates: { latitude: 53.3419, longitude: -6.3095 } },
      { name: 'Phoenix Park', address: 'Phoenix Park, Dublin', coordinates: { latitude: 53.3563, longitude: -6.3298 } },
      { name: 'Ha penny Bridge', address: 'Bachelors Walk, Dublin', coordinates: { latitude: 53.3462, longitude: -6.2631 } },
    ],
  },
  edinburgh: {
    countryCode: 'GB',
    landmarks: [
      { name: 'Edinburgh Castle', address: 'Castlehill, Edinburgh', coordinates: { latitude: 55.9486, longitude: -3.1999 } },
      { name: 'Royal Mile', address: 'Royal Mile, Edinburgh', coordinates: { latitude: 55.95, longitude: -3.187 } },
      { name: "Arthur's Seat", address: 'Holyrood Park, Edinburgh', coordinates: { latitude: 55.9445, longitude: -3.1618 } },
      { name: 'Palace of Holyroodhouse', address: 'Canongate, Edinburgh', coordinates: { latitude: 55.952, longitude: -3.1724 } },
      { name: 'Calton Hill', address: 'Calton Hill, Edinburgh', coordinates: { latitude: 55.9552, longitude: -3.182 } },
      { name: 'National Museum of Scotland', address: 'Chambers St, Edinburgh', coordinates: { latitude: 55.947, longitude: -3.1909 } },
      { name: "St Giles' Cathedral", address: 'High St, Edinburgh', coordinates: { latitude: 55.9499, longitude: -3.1905 } },
      { name: 'Dean Village', address: 'Dean Path, Edinburgh', coordinates: { latitude: 55.9528, longitude: -3.2172 } },
    ],
  },
  'mexico city': {
    countryCode: 'MX',
    landmarks: [
      { name: 'Zocalo', address: 'Plaza de la Constitucion, Mexico City', coordinates: { latitude: 19.4326, longitude: -99.1332 } },
      { name: 'Palacio de Bellas Artes', address: 'Av. Juarez, Mexico City', coordinates: { latitude: 19.4352, longitude: -99.1412 } },
      { name: 'Chapultepec Castle', address: 'Bosque de Chapultepec, Mexico City', coordinates: { latitude: 19.4204, longitude: -99.1819 } },
      { name: 'Angel of Independence', address: 'Paseo de la Reforma, Mexico City', coordinates: { latitude: 19.427, longitude: -99.1677 } },
      { name: 'Frida Kahlo Museum', address: 'Londres 247, Coyoacan, Mexico City', coordinates: { latitude: 19.3552, longitude: -99.1626 } },
      { name: 'Templo Mayor', address: 'Seminario 8, Centro Historico, Mexico City', coordinates: { latitude: 19.4343, longitude: -99.131 } },
      { name: 'Xochimilco Canals', address: 'Xochimilco, Mexico City', coordinates: { latitude: 19.2575, longitude: -99.1046 } },
      { name: 'Basilica of Our Lady of Guadalupe', address: 'Villa Gustavo A. Madero, Mexico City', coordinates: { latitude: 19.4849, longitude: -99.1179 } },
    ],
  },
  'rio de janeiro': {
    countryCode: 'BR',
    landmarks: [
      { name: 'Christ the Redeemer', address: 'Parque Nacional da Tijuca, Rio de Janeiro', coordinates: { latitude: -22.9519, longitude: -43.2105 } },
      { name: 'Sugarloaf Mountain', address: 'Av. Pasteur 520, Rio de Janeiro', coordinates: { latitude: -22.9486, longitude: -43.1566 } },
      { name: 'Copacabana Beach', address: 'Copacabana, Rio de Janeiro', coordinates: { latitude: -22.9711, longitude: -43.1822 } },
      { name: 'Ipanema Beach', address: 'Ipanema, Rio de Janeiro', coordinates: { latitude: -22.983, longitude: -43.2049 } },
      { name: 'Selaron Steps', address: 'R. Manuel Carneiro, Rio de Janeiro', coordinates: { latitude: -22.915, longitude: -43.179 } },
      { name: 'Maracana Stadium', address: 'Av. Pres. Castelo Branco, Rio de Janeiro', coordinates: { latitude: -22.9122, longitude: -43.2302 } },
      { name: 'Lage Park', address: 'R. Jardim Botanico 414, Rio de Janeiro', coordinates: { latitude: -22.9606, longitude: -43.213 } },
      { name: 'Museum of Tomorrow', address: 'Praca Maua 1, Rio de Janeiro', coordinates: { latitude: -22.8944, longitude: -43.1803 } },
    ],
  },
  'buenos aires': {
    countryCode: 'AR',
    landmarks: [
      { name: 'Plaza de Mayo', address: 'Plaza de Mayo, Buenos Aires', coordinates: { latitude: -34.6081, longitude: -58.3702 } },
      { name: 'Casa Rosada', address: 'Balcarce 50, Buenos Aires', coordinates: { latitude: -34.6081, longitude: -58.3702 } },
      { name: 'La Boca Caminito', address: 'Caminito, Buenos Aires', coordinates: { latitude: -34.6345, longitude: -58.3631 } },
      { name: 'Recoleta Cemetery', address: 'Junin 1760, Buenos Aires', coordinates: { latitude: -34.5881, longitude: -58.3932 } },
      { name: 'Teatro Colon', address: 'Cerrito 628, Buenos Aires', coordinates: { latitude: -34.6011, longitude: -58.3831 } },
      { name: 'Obelisco', address: 'Av. 9 de Julio, Buenos Aires', coordinates: { latitude: -34.6037, longitude: -58.3816 } },
      { name: 'Puerto Madero', address: 'Puerto Madero, Buenos Aires', coordinates: { latitude: -34.6118, longitude: -58.3633 } },
      { name: 'Palermo Soho', address: 'Palermo, Buenos Aires', coordinates: { latitude: -34.5886, longitude: -58.4306 } },
    ],
  },
  marrakech: {
    countryCode: 'MA',
    landmarks: [
      { name: 'Jemaa el-Fnaa', address: 'Medina, Marrakech', coordinates: { latitude: 31.6258, longitude: -7.9892 } },
      { name: 'Koutoubia Mosque', address: 'Avenue Mohammed V, Marrakech', coordinates: { latitude: 31.6236, longitude: -7.9936 } },
      { name: 'Bahia Palace', address: 'Rue Riad Zitoun el Jdid, Marrakech', coordinates: { latitude: 31.6215, longitude: -7.9811 } },
      { name: 'Majorelle Garden', address: 'Rue Yves Saint Laurent, Marrakech', coordinates: { latitude: 31.6416, longitude: -8.0016 } },
      { name: 'Yves Saint Laurent Museum', address: 'Rue Yves Saint Laurent, Marrakech', coordinates: { latitude: 31.6417, longitude: -8.0026 } },
      { name: 'Ben Youssef Madrasa', address: 'Rue Assouel, Marrakech', coordinates: { latitude: 31.6315, longitude: -7.9898 } },
      { name: 'Saadian Tombs', address: 'Rue de La Kasbah, Marrakech', coordinates: { latitude: 31.6174, longitude: -7.987 } },
      { name: 'El Badi Palace', address: 'Kasbah, Marrakech', coordinates: { latitude: 31.6179, longitude: -7.9868 } },
      { name: 'Le Jardin Secret', address: '121 Rue Mouassine, Marrakech', coordinates: { latitude: 31.6282, longitude: -7.9923 } },
      { name: 'Menara Gardens', address: 'Av. de la Menara, Marrakech', coordinates: { latitude: 31.6119, longitude: -8.0085 } },
      { name: 'Dar Si Said Museum', address: '8 Rue de la Bahia, Marrakech', coordinates: { latitude: 31.6197, longitude: -7.9825 } },
      { name: 'Ali ben Youssef Mosque', address: 'Rue Assouel, Marrakech', coordinates: { latitude: 31.6311, longitude: -7.9888 } },
    ],
  },
  doha: {
    countryCode: 'QA',
    landmarks: [
      { name: 'Souq Waqif', address: 'Al Souq St, Doha', coordinates: { latitude: 25.2879, longitude: 51.5332 } },
      { name: 'Museum of Islamic Art', address: 'Corniche, Doha', coordinates: { latitude: 25.2951, longitude: 51.5392 } },
      { name: 'The Pearl-Qatar', address: 'The Pearl, Doha', coordinates: { latitude: 25.3706, longitude: 51.5505 } },
      { name: 'Katara Cultural Village', address: 'Katara, Doha', coordinates: { latitude: 25.3596, longitude: 51.5255 } },
      { name: 'National Museum of Qatar', address: 'Museum Park St, Doha', coordinates: { latitude: 25.2867, longitude: 51.5433 } },
      { name: 'Doha Corniche', address: 'Corniche, Doha', coordinates: { latitude: 25.3205, longitude: 51.5332 } },
      { name: 'Villaggio Mall', address: 'Al Waab St, Doha', coordinates: { latitude: 25.2596, longitude: 51.4427 } },
      { name: 'State Grand Mosque', address: 'Al Muhandiseen St, Doha', coordinates: { latitude: 25.3053, longitude: 51.5126 } },
    ],
  },
};

const CITY_ALIASES: Record<string, string> = {
  'new york city': 'new york',
  nyc: 'new york',
  roma: 'rome',
  londres: 'london',
  'paris france': 'paris',
  'tokyo japan': 'tokyo',
  'barcelona spain': 'barcelona',
  'amsterdam netherlands': 'amsterdam',
  'dubai uae': 'dubai',
  'istanbul turkey': 'istanbul',
  lisboa: 'lisbon',
  'lisbon portugal': 'lisbon',
  'madrid spain': 'madrid',
  'prague czech republic': 'prague',
  wien: 'vienna',
  'vienna austria': 'vienna',
  'budapest hungary': 'budapest',
  'athens greece': 'athens',
  seoul: 'seoul',
  'seoul south korea': 'seoul',
  bangkok: 'bangkok',
  'bangkok thailand': 'bangkok',
  hk: 'hong kong',
  'hong kong china': 'hong kong',
  'sydney australia': 'sydney',
  la: 'los angeles',
  'los angeles usa': 'los angeles',
  sf: 'san francisco',
  'san francisco usa': 'san francisco',
  'cairo egypt': 'cairo',
  'cape town south africa': 'cape town',
  'osaka japan': 'osaka',
  'kyoto japan': 'kyoto',
  'berlin germany': 'berlin',
  'dublin ireland': 'dublin',
  'edinburgh scotland': 'edinburgh',
  cdmx: 'mexico city',
  'mexico city mexico': 'mexico city',
  rio: 'rio de janeiro',
  'rio de janeiro brazil': 'rio de janeiro',
  ba: 'buenos aires',
  'buenos aires argentina': 'buenos aires',
  'marrakech morocco': 'marrakech',
  marrakesh: 'marrakech',
  'marrakesh morocco': 'marrakech',
  'marrakech morroco': 'marrakech',
  'marrakesh morroco': 'marrakech',
  'doha qatar': 'doha',
};

const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s,-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const inferCityTokens = (city: string): string[] => {
  const normalized = normalizeText(city);
  if (!normalized) return [];
  const parts = normalized.split(',').map((p) => p.trim()).filter(Boolean);
  const first = parts[0] || normalized;
  const tokens = new Set<string>([
    normalized,
    first,
    first.split('-')[0]?.trim() || first,
  ]);
  for (const part of parts) tokens.add(part);
  return Array.from(tokens).filter(Boolean);
};

export const resolveBaselineCityKey = (
  city: string,
  countryCode?: string,
): string | null => {
  const wantedCountry = countryCode?.trim().toUpperCase();
  for (const token of inferCityTokens(city)) {
    const aliased = CITY_ALIASES[token] || token;
    if (!(aliased in BASELINE_CITY_LANDMARKS)) continue;
    if (!wantedCountry) return aliased;
    const entry = BASELINE_CITY_LANDMARKS[aliased];
    if (entry.countryCode === wantedCountry) return aliased;
  }
  return null;
};

export const getBaselineLandmarksForCity = (
  city: string,
  options?: { countryCode?: string; limit?: number },
): BaselineLandmarkSeed[] => {
  const key = resolveBaselineCityKey(city, options?.countryCode);
  if (!key) return [];
  const rows = BASELINE_CITY_LANDMARKS[key]?.landmarks || [];
  const limit = Math.max(1, options?.limit ?? rows.length);
  return rows.slice(0, limit);
};

export const listBaselineCityKeys = (): string[] => Object.keys(BASELINE_CITY_LANDMARKS);
